import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import discountApi from '../../../services/discountApi';
import { formatCurrency } from '../../../utils/baseUtils';
import { cn } from '../../../components/ui/_cn';
import FinBadge from '../../connect/finance/fin-badge';
import FinButton from '../../connect/finance/fin-button';
import { FinTableSkeleton } from '../../connect/finance/fin-skeleton';
import OrderReportPager from '../../finance/pages/order-report-pager';
import { menuNamesLabel } from './coupon-menus';
import { legacyStatusTag } from '../lib/campaign-status';

// The Legacy discounts card (spec 2026-10-21 §2.3, handoff §1): today's
// coupons in the Coupons page's table, behaviour unchanged. The server
// filters `isactive`, so its paging matches the rows shown. Active /
// Expired is read from `expiredate`.
export const LEGACY_PAGE_SIZE = 10;

export const LEGACY_COPY = {
  title: 'Legacy discounts',
  description: 'Percentage coupons from the previous system.',
  add: 'Add legacy coupon',
  allMenus: 'All menus',
  pages: 'Legacy coupon pages',
  empty: 'No legacy coupons.',
  loadError: 'Couldn’t load legacy coupons.',
  retry: 'Retry',
  showing: (first, lastRow, total) => `Showing ${first}–${lastRow} of ${total}`,
};

const HEADERS = [
  ['Coupon', ''],
  ['Min. spend', 'text-right'],
  ['Rate', 'text-right'],
  ['Menus', ''],
  ['Used', 'text-right'],
  ['Total discount', 'text-right'],
  ['Status', ''],
];

// Coupon (name over code), Min. spend, Rate, Menus, Used, Total discount,
// Status, the pencil. Below sm: coupon | status, then rate | total.
const GRID =
  'grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 sm:grid-cols-[minmax(0,1.6fr)_88px_64px_minmax(0,1.2fr)_56px_112px_96px_32px] sm:gap-y-0';

const LegacyRow = ({ coupon, isLast, onEdit }) => {
  const tag = legacyStatusTag(coupon.expiredate);
  const menus = menuNamesLabel(coupon.menunames, LEGACY_COPY.allMenus);
  const restricted = menus !== LEGACY_COPY.allMenus;
  const open = () => onEdit(coupon);
  return (
    <div
      role="row"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        // Only the row itself: an Enter on the pencil is that button's click.
        if (event.target === event.currentTarget && event.key === 'Enter') open();
      }}
      className={cn(
        GRID,
        'cursor-pointer items-center px-5 py-[11px] text-[13.5px] hover:bg-fin-hover focus:bg-fin-hover focus:outline-none',
        !isLast && 'border-b border-fin-hair',
      )}
    >
      <div role="cell" className="min-w-0">
        <p className="truncate font-semibold text-fin-ink" title={coupon.name}>
          {coupon.name}
        </p>
        <p className="truncate font-mono text-[12px] text-fin-muted">{coupon.code}</p>
      </div>
      <div role="cell" className="hidden text-right tabular-nums text-fin-ink sm:block">
        {formatCurrency(coupon.minprice)}
      </div>
      <div role="cell" className="tabular-nums text-fin-ink sm:text-right">
        <span className="text-[12px] text-fin-muted sm:hidden">Rate </span>
        <span>{coupon.rate ? `${coupon.rate}%` : formatCurrency(coupon.fixrate)}</span>
      </div>
      <div role="cell" className="hidden min-w-0 sm:block">
        <span
          title={restricted ? menus : undefined}
          className={cn('block truncate', restricted ? 'text-fin-ink' : 'text-fin-muted')}
        >
          {menus}
        </span>
      </div>
      <div role="cell" className="hidden text-right tabular-nums text-fin-ink sm:block">
        {coupon.usagecount ?? 0}
      </div>
      <div role="cell" className="text-right tabular-nums text-fin-ink">
        <span className="text-[12px] text-fin-muted sm:hidden">Total </span>
        <span className="font-bold">{formatCurrency(coupon.totaldiscount)}</span>
      </div>
      <div role="cell" className="col-start-2 row-start-1 justify-self-end sm:col-start-auto sm:row-start-auto sm:justify-self-start">
        <FinBadge tone={tag.tone} label={tag.label} />
      </div>
      <div role="cell" className="hidden justify-end sm:flex">
        <button
          type="button"
          aria-label={`Edit ${coupon.name}`}
          onClick={(event) => {
            event.stopPropagation();
            open();
          }}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-fin-muted hover:bg-fin-neutral-pale hover:text-fin-ink"
        >
          <PencilIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

const LegacyCouponList = ({ companyId, reloadKey = 0, onAdd, onEdit }) => {
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ rows: [], total: 0, loading: true, failed: false });
  const request = useRef(0);

  const load = useCallback(async () => {
    if (!companyId) return;
    const id = ++request.current;
    setState((prev) => ({ ...prev, loading: true, failed: false }));
    try {
      const res = await discountApi.list(
        `?companyid=${encodeURIComponent(companyId)}&isactive=true&pageNumber=${page}&pageSize=${LEGACY_PAGE_SIZE}`,
      );
      if (id !== request.current) return;
      const rows = res?.data?.data || [];
      const total = typeof res?.data?.totalRecords === 'number' ? res.data.totalRecords : rows.length;
      // A delete can empty the last page and the api does not clamp
      // pageNumber: step back to the last page that holds rows, staying in
      // the loading state, rather than paint "No legacy coupons." over a
      // card that still has coupons.
      if (rows.length === 0 && total > 0 && page > 1) {
        setPage(Math.min(page - 1, Math.max(1, Math.ceil(total / LEGACY_PAGE_SIZE))));
        return;
      }
      setState({ rows, total, loading: false, failed: false });
    } catch {
      if (id !== request.current) return;
      setState({ rows: [], total: 0, loading: false, failed: true });
    }
  }, [companyId, page]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  useEffect(() => {
    setPage(1);
  }, [companyId]);

  const { rows, total, loading, failed } = state;
  const pageCount = Math.max(1, Math.ceil(total / LEGACY_PAGE_SIZE));
  const first = (page - 1) * LEGACY_PAGE_SIZE + 1;
  const lastRow = Math.min(page * LEGACY_PAGE_SIZE, total);

  let body;
  if (failed) {
    body = (
      <div className="flex min-h-[200px] flex-wrap items-center justify-center gap-3 border-t border-fin-border px-5 text-center">
        <p className="text-[13px] text-fin-red">{LEGACY_COPY.loadError}</p>
        <FinButton onClick={() => load()}>{LEGACY_COPY.retry}</FinButton>
      </div>
    );
  } else if (loading && rows.length === 0) {
    body = (
      <div className="border-t border-fin-border px-5">
        <FinTableSkeleton rows={LEGACY_PAGE_SIZE} />
      </div>
    );
  } else {
    body = (
      <div className="border-t border-fin-border">
        <div role="table" aria-label={LEGACY_COPY.title}>
          <div
            role="row"
            className={cn(GRID, 'hidden border-b border-fin-border bg-fin-recess px-5 py-2.5 text-[12px] font-medium text-fin-faint sm:grid')}
          >
            {HEADERS.map(([title, align]) => (
              <div key={title} role="columnheader" className={align || undefined}>
                {title}
              </div>
            ))}
            <div role="columnheader">
              <span className="sr-only">Edit</span>
            </div>
          </div>
          <div role="rowgroup">
            {rows.map((coupon, index) => (
              <LegacyRow key={coupon.id} coupon={coupon} isLast={index === rows.length - 1} onEdit={onEdit} />
            ))}
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="flex min-h-[160px] items-center justify-center px-5 text-center">
            <p className="text-[13px] text-fin-muted">{LEGACY_COPY.empty}</p>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <section aria-labelledby="legacy-discounts-title" className="overflow-hidden rounded-xl border border-fin-border bg-fin-surface">
      <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 id="legacy-discounts-title" className="text-[15px] font-bold text-fin-ink">
            {LEGACY_COPY.title}
          </h2>
          <p className="text-[13px] text-fin-muted">{LEGACY_COPY.description}</p>
        </div>
        {/* Secondary: one primary button per page (handoff §0). */}
        <FinButton onClick={onAdd}>
          <PlusIcon className="mr-1.5 h-4 w-4 stroke-[2.5]" aria-hidden="true" />
          {LEGACY_COPY.add}
        </FinButton>
      </div>
      {body}
      {!failed && total > 0 && rows.length > 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-fin-border px-5 py-3">
          <p className="text-[13px] text-fin-muted">{LEGACY_COPY.showing(first, lastRow, total)}</p>
          {pageCount > 1 ? (
            <OrderReportPager label={LEGACY_COPY.pages} page={page} pageCount={pageCount} onChange={setPage} />
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default LegacyCouponList;
